<template>
  <div 
    class="confirm-button-wrapper relative flex w-max items-center justify-center"
    :style="{ opacity: disabled ? 0.5 : 1 }"
  >
    <div class="menu-trigger-wrapper flex" @click="!disabled && toggleMenu()">
      <slot name="menu-trigger">
        <SimpleButton
          :icon="buttonIcon"
          :text="buttonText"
          :active="activeButton"
          :disabled="disabled"
          :size="size"
        />
      </slot>
    </div>
    <SmallPopup
      :show="showMenu"
      :showClose="false"
      cardClass="bg-popover-solid p-4"
      @close="showMenu = false"
    >
      <slot name="popup">
        <div class="flex w-72 flex-col items-center gap-1 self-center text-center">
          <div class="mb-2 flex size-11 items-center justify-center rounded-full bg-red-soft">
            <i class="tr tr-warning text-xl text-red"></i>
          </div>
          <div v-if="popupTitle" class="text-group font-bold text-text">{{ popupTitle }}</div>
          <div v-if="popupSubTitle" class="text-body text-text-muted wrap-anywhere">{{ popupSubTitle }}</div>
        </div>
        <slot name="popup-content"></slot>
        <div class="mt-2 flex gap-2">
          <SimpleButton
            v-if="showCancelButton"
            class="flex-1"
            :text="cancelButtonText || $t('task.manual_CANCEL')"
            @click="emit('cancel'); showMenu = false;"/>
          <SimpleButton
            class="flex-1"
            red
            :text="confirmButtonText || $t('task.manual_YES')"
            :disabled="confirmIsDisabled"
            @click="confirm"/>
        </div>
      </slot>
    </SmallPopup>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { useEventListener } from '@vueuse/core';
import SimpleButton from '@/components/elements/SimpleButton';
import SmallPopup from '@/components/elements/SmallPopup';

const props = defineProps({
  buttonText: String,
  size: {
    type: String,
    default: 'auto',
    validator: (value) => ['auto', 'xs', 'sm', 'md', 'lg', 'icon'].includes(value),
  },
  popupTitle: String,
  popupSubTitle: String,
  cancelButtonText: String,
  confirmButtonText: String,
  buttonIcon: String,
  activeButton: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  confirmIsDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancel', 'confirm', 'popup-open', 'popup-close']);

const showMenu = ref(false);

const toggleMenu = async () => {
  showMenu.value = !showMenu.value;
  if (showMenu.value) {
    await nextTick();
  }
};

const confirm = () => {
  emit('confirm');
  showMenu.value = false;
};

useEventListener(document, 'keydown', (e) => {
  if (!showMenu.value) return;
  if (e.key === 'Escape') {
    e.stopPropagation();
    showMenu.value = false;
  }
  if (e.key === 'Enter' && !props.confirmIsDisabled) {
    e.preventDefault();
    e.stopPropagation();
    confirm();
  }
}, true);

watch(showMenu, () => {
  if (showMenu.value) emit('popup-open');
  else emit('popup-close');
});

// for a trigger that can't sit inside this component, e.g. a row action in a popover that closes first
defineExpose({
  open: () => {
    showMenu.value = true;
  },
});
</script>

